* @usage:
*
* <calico-checkbox value="foo" v-model="mySelectedValue" />
* <calico-checkbox value="bar" v-model="mySelectedValue" />
* <calico-checkbox value="baz" v-model="mySelectedValue" />
*
* data() {
*   return {
*     mySelectedValue: "", // Bound to v-model
*   };
* }
*
* Props:
* - modelValue: [String, Boolean, Number] - The current value of the checkbox (used with v-model).
* - disabled: Boolean - Disables the checkbox when true.
* - error: Boolean - Adds an error state when true.
*
* Emits:
* - 'update:modelValue': Updates the value when the checkbox is clicked.
*/

<template>
  <div class="calico-checkbox">
    <label
      class="body--medium"
      :class="{
        checked,
        error: error && !checked,
        disabled
      }"
      @click.stop
    >
      <input
        type="checkbox"
        :checked="checked"
        :disabled="disabled"
        v-bind="filteredAttrs"
        v-on="filteredListeners"
        @change="handleChange"
      >
      <div>
        <span
          :class="{
            'checkbox': true,
            checked,
            error: error && !checked,
            disabled
          }"
        >
          <i
            v-if="checked"
            class="bi bi-check2"
          />
        </span>
      </div>
      <slot />
    </label>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'CalicoCheckbox',
  props: {
    modelValue: {
      type: [String, Boolean, Number],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const filteredAttrs = computed(() => {
      const { ...restAttrs } = attrs;
      return restAttrs;
    });

    const filteredListeners = computed(() => {
      const listeners = { ...attrs };
      delete listeners.onUpdateModelValue;
      return listeners;
    });

    const checked = computed(() => props.modelValue === attrs.value);

    const handleChange = (event) => {
      const value = event.target.checked ? attrs.value : '';
      emit('update:modelValue', value);
    };

    return {
      filteredAttrs,
      filteredListeners,
      checked,
      handleChange,
    };
  },
};
</script>

<style lang="scss">
.calico-checkbox {
  label {
    @include inline-space($space-small);
    margin: 0;
    input {
      display: none;
    }
    .bi {
      display: flex;
      align-items: center;
      font-size: $icon-small;
    }
    .checkbox {
      height: $icon-medium;
      width: $icon-medium;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: $radius-small;
      border: $border-width-default solid $color-neutral-light;
      cursor: pointer;
    }
    .checked {
      color: $color-white;
      border: $border-width-default solid $color-black;
      background: $color-black;
    }
    .error {
      border: $border-width-default solid $color-semantics-error !important;
    }
    .disabled {
      background: $color-neutral-light !important;
      border: $border-width-default solid $color-neutral-light !important;
      cursor: default !important;
    }
  }

  .error {
    color: $color-semantics-error
  }
  .disabled {
    color: $color-text-secondary
  }
}

</style>
